"use client";

import { type FC, isValidElement, type ReactNode } from "react";
import {
  formatNumber,
  formatNumberAsMoney,
  isEmpty,
  isNumber,
  isObject,
  isString,
} from "@huub-web/utils";

import type { ValueType } from "../../types";
import { Text } from "../Text";

export type ValueParserProps = {
  value: ValueType;
  align?: "left" | "right";
};

const parseNumber = (value: string | number): number =>
  isString(value) ? parseFloat(value) : value;

/**
 * ValueParser component is a component that parses a value and displays it in a formatted way.
 */
export const ValueParser: FC<ValueParserProps> = ({ align, value }) => {
  const display: ReactNode = (() => {
    if (isString(value) && value === "") {
      return (
        <Text variant="bodyS" italic align={align}>
          Onbekend
        </Text>
      );
    }

    if (isNumber(value)) {
      return formatNumber(value);
    }

    if (isValidElement(value)) {
      return value;
    }

    if (isObject(value)) {
      const parsedValue = value.value as string | number;

      if (isEmpty(parsedValue)) {
        return (
          <Text variant="bodyS" italic align={align}>
            Onbekend
          </Text>
        );
      }

      if (value.type === "money") {
        return formatNumberAsMoney(parseNumber(parsedValue));
      }
      if (value.type === "number") {
        return formatNumber(parseNumber(parsedValue));
      }
      if (value.type === "percentage") {
        return `${formatNumber(parseNumber(parsedValue))}%`;
      }
      if (value.type === "percentageWithPlus") {
        const number = parseNumber(parsedValue);
        if (number > 0) {
          return `+${formatNumber(number)}%`;
        }
        return `${formatNumber(parseNumber(parsedValue))}%`;
      }
      if (value.type === "squareMeter") {
        return `${formatNumber(parseNumber(parsedValue))} m²`;
      }

      return parsedValue;
    }

    return value as string;
  })();

  return display;
};
